/**
 * useDataOrchestrator - Central coordinator for event-driven data updates
 *
 * Architecture (WebSocket-first for speed):
 * - WebSocket data is primary (fast, real-time)
 * - Diamondback seeding is async/background (doesn't block UI)
 * - Block event-driven metric calculations
 * - Intelligent selective API refreshes
 *
 * Usage:
 * const { data, blockMetrics, isLoading, refreshEndpoints } = useDataOrchestrator({
 *   mempoolWebSocket,
 *   apiKey
 * });
 */

import { useState, useEffect, useRef } from 'react';
import { BlockEventManager } from './blockEventManager';
import { SmartPollingManager } from './smartPolling';

export const useDataOrchestrator = ({ mempoolWebSocket, apiKey }) => {
  const [data, setData] = useState(null);
  const [dataMetadata, setDataMetadata] = useState({});
  const [diamondbackData, setDiamondbackData] = useState(null);
  const [blockMetrics, setBlockMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const blockEventManager = useRef(new BlockEventManager());
  const pollingManager = useRef(new SmartPollingManager(apiKey));

  // Initialize - fetch Sidewinder first (fast), seed Diamondback in background
  useEffect(() => {
    const fetchInitialData = async () => {
      console.log('[DataOrchestrator] Initializing (WebSocket-first architecture)...');
      setIsLoading(true);

      try {
        // Step 1a: Fetch critical rate/price endpoints FIRST so currency conversion
        // and BitcoinPrice consumers unblock in ~200-500ms instead of waiting for
        // the slowest of ~22 endpoints (up to 10s per axios timeout).
        console.log('[DataOrchestrator] Fetching critical endpoints (s17, s06)...');
        pollingManager.current.fetchSidewinderEndpoints(['s17', 's06']).then((fast) => {
          if (fast?.data) {
            setData((prev) => ({ ...(prev || {}), ...fast.data }));
            setDataMetadata((prev) => ({ ...(prev || {}), ...fast.metadata }));
          }
        }).catch((err) => {
          console.warn('[DataOrchestrator] Fast-path fetch failed (non-critical):', err.message);
        });

        // Step 1b: Fetch everything (includes s17/s06 again, harmless merge)
        console.log('[DataOrchestrator] Fetching all Sidewinder + Diamondback endpoints...');
        const result = await pollingManager.current.fetchAllEndpoints();
        setData((prev) => ({ ...(prev || {}), ...result.data }));
        setDataMetadata((prev) => ({ ...(prev || {}), ...result.metadata }));
        setDiamondbackData(result.diamondback);

        console.log('[DataOrchestrator] Initial data loaded:', {
          sidewinderEndpoints: Object.keys(result.data).length,
          diamondbackEndpoints: Object.keys(result.diamondback || {}).length,
          success: result.summary.success,
          errors: result.summary.error
        });

        // Mark loading complete - UI can render now
        setIsLoading(false);

        // Step 2: Seed BlockEventManager from Diamondback in BACKGROUND (non-blocking)
        console.log('[DataOrchestrator] Seeding block history from Diamondback (background)...');
        blockEventManager.current.seedFromDiamondback().then(seedResult => {
          if (seedResult.success) {
            // Calculate metrics from seeded blocks (enhances WebSocket data)
            const initialMetrics = {
              hashrate: blockEventManager.current.calculateHashrate(),
              avgBlockTime: blockEventManager.current.calculateAvgBlockTime(),
              difficultyProgress: blockEventManager.current.calculateDifficultyProgress(
                blockEventManager.current.getLatestBlock()
              ),
              poolDistribution: blockEventManager.current.calculatePoolDistribution(),
              blocks24h: blockEventManager.current.calculateBlocks24h()
            };

            setBlockMetrics(initialMetrics);
            console.log('[DataOrchestrator] Block metrics enhanced from Diamondback seed');
          } else {
            console.log('[DataOrchestrator] Diamondback seed skipped - using WebSocket data');
          }
        }).catch(err => {
          console.warn('[DataOrchestrator] Diamondback seed failed (non-critical):', err.message);
        });

      } catch (error) {
        console.error('[DataOrchestrator] Failed to fetch initial data:', error);
        setIsLoading(false);
      }
    };

    // Fetch immediately — WebSockets are on different domains and don't compete
    fetchInitialData();

    // Start interval-based polling
    const manager = pollingManager.current;
    manager.start();

    // Cleanup on unmount
    return () => {
      console.log('[DataOrchestrator] Cleaning up');
      manager.cleanup();
    };
  }, [apiKey]);

  // Listen to Mempool WebSocket for new blocks
  useEffect(() => {
    if (!mempoolWebSocket || !mempoolWebSocket.message) return;

    try {
      const message = JSON.parse(mempoolWebSocket.message);

      // Handle new block events
      if (message.block) {
        const block = message.block;

        console.log(`[DataOrchestrator] New block ${block.height} from ${block.extras?.pool?.name || 'Unknown'}`);

        // Process block through BlockEventManager
        blockEventManager.current.onNewBlock(block, {
          // Update calculated metrics
          onMetricsCalculated: (metrics) => {
            setBlockMetrics(metrics);
            console.log('[DataOrchestrator] Block metrics updated:', {
              hashrate: metrics.hashrate ? `${metrics.hashrate.toFixed(2)} EH/s` : 'N/A',
              avgBlockTime: metrics.avgBlockTime ? `${metrics.avgBlockTime.toFixed(0)}s` : 'N/A',
              progress: metrics.difficultyProgress ? `${metrics.difficultyProgress.progress.toFixed(1)}%` : 'N/A'
            });
          },

          // Trigger selective API refresh
          onRefreshNeeded: async (endpoints) => {
            console.log(`[DataOrchestrator] Refreshing after block`);
            const result = await pollingManager.current.onBlockEvent();

            // Merge updated Sidewinder data
            setData(prev => ({
              ...prev,
              ...result.data
            }));

            setDataMetadata(prev => ({
              ...prev,
              ...result.metadata
            }));

            // Merge updated Diamondback data
            if (result.diamondback) {
              setDiamondbackData(prev => ({
                ...prev,
                ...result.diamondback
              }));
            }
          }
        });
      }
    } catch (error) {
      // Silently ignore parse errors (not all messages are JSON)
      if (error instanceof SyntaxError) return;
      console.error('[DataOrchestrator] Error processing WebSocket message:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mempoolWebSocket?.message]);

  /**
   * Manually refresh specific Sidewinder endpoints
   * @param {Array} endpoints - Array of endpoint names
   */
  const refreshEndpoints = async (endpoints) => {
    console.log(`[DataOrchestrator] Manual refresh of ${endpoints.length} endpoints`);
    const result = await pollingManager.current.fetchSidewinderEndpoints(endpoints);

    setData(prev => ({
      ...prev,
      ...result.data
    }));

    setDataMetadata(prev => ({
      ...prev,
      ...result.metadata
    }));

    return result;
  };

  /**
   * Refresh on-demand endpoints (price, market data)
   */
  const refreshOnDemand = async () => {
    console.log('[DataOrchestrator] Refreshing on-demand endpoints');
    const result = await pollingManager.current.refreshOnDemand();

    setData(prev => ({
      ...prev,
      ...result.data
    }));

    setDataMetadata(prev => ({
      ...prev,
      ...result.metadata
    }));

    return result;
  };

  /**
   * Manually refresh Diamondback endpoints
   * @param {Array} endpointKeys - Array of Diamondback endpoint keys
   */
  const refreshDiamondback = async (endpointKeys) => {
    console.log(`[DataOrchestrator] Refreshing Diamondback endpoints: ${endpointKeys.join(', ')}`);
    const result = await pollingManager.current.fetchDiamondback(endpointKeys);

    setDiamondbackData(prev => ({
      ...prev,
      ...result
    }));

    return result;
  };

  return {
    // Sidewinder data
    data,
    dataMetadata,

    // Diamondback data
    diamondbackData,

    // Calculated metrics
    blockMetrics,

    // Loading state
    isLoading,

    // Block info
    lastBlockData: blockEventManager.current.getLatestBlock(),
    blockHistory: blockEventManager.current.getBlockHistory(),
    blockHistoryStats: blockEventManager.current.getStats(),

    // Manual refresh
    refreshEndpoints,
    refreshOnDemand,
    refreshDiamondback,

    // Status
    pollingStatus: pollingManager.current.getStatusReport()
  };
};
